/**
 * Shared timing and type scale for "beats" — the flow's one-after-another
 * text moments, on the intro screen and on every handoff.
 *
 * The mechanic is always the same: the first line stays on screen and shrinks
 * back to make room, and the next line opens under it. Nothing is unmounted,
 * so the reader never loses what they just read.
 *
 * The shrink is a transform, not a font-size change, and the wrapper's height
 * is animated to match — ShrinkingBeat documents why. Beat copy other than
 * the headline (leads, bodies) is never resized at all; it belongs to one
 * beat and simply collapses away with it.
 */

/**
 * How far a superseded beat shrinks. This is a `transform: scale`, never a
 * font-size — see ShrinkingBeat for why that distinction is the whole point.
 * 0.56 of a 34px headline lands at ~19px, the size the design steps back to.
 */
export const SHRUNK_SCALE = 0.56;

/** Opacity a beat holds at once it's been superseded — dimmed, never gone. */
export const SHRUNK_OPACITY = 0.4;

/**
 * The curve every part of a beat change runs on — the outgoing headline's
 * shrink, its wrapper height, the collapsing lead/body, and the incoming
 * line's rise. One curve for all of them so a beat reads as a single move.
 *
 * Three properties of a curve decide whether that move feels smooth, and all
 * three are worth measuring rather than eyeballing:
 *
 *   the departure — how long the type sits near-still before it goes. Under
 *     ~200ms the move starts the instant the beat does, and reads as a shove
 *     no matter how gently it ends.
 *   peak velocity — how much faster the fastest moment is than the average.
 *     The higher it is, the more the type lurches mid-flight. What the eye
 *     judges is peak ÷ duration, so a longer beat can afford a steeper curve.
 *   where it finishes — the point the movement is ~95% done. Anything after
 *     that is duration the eye reads as already over, so it registers as a
 *     drift rather than a landing.
 *
 * Every curve this has been through failed on one of the three.
 * [0.2, 0.8, 0.2, 1] put ~67% of the move in the first 110ms: a snap, then a
 * crawl. [0.45, 0.05, 0.25, 1] lurched at 2.58x and was done at t=0.74,
 * leaving a quarter of the duration as dead tail. [0.42, 0, 0.4, 1] fixed the
 * tail but still departed after 135ms, which is the shove.
 *
 * This one sits still for 222ms, is only 41% through at the halfway point —
 * so it is still travelling when a front-loaded curve would have parked — and
 * finishes at t=0.81. It costs 2.51x peak against the previous 2.07x, but
 * over a longer beat that is 2.51/s versus 2.31/s: the start softens without
 * the middle gaining a lurch. Plain ease-in-out-cubic [0.65, 0, 0.35, 1] has
 * the same departure and would have cost 3.17/s; the softer second control
 * point is what buys the difference.
 */
export const BEAT_TRANSITION = {
  duration: 1.0,
  ease: [0.65, 0, 0.45, 1] as const,
};

/**
 * How long the opening beat holds alone before the next one opens under it.
 * Long enough to read a short headline once, not long enough to feel like the
 * screen has stalled.
 */
export const BEAT_HOLD_MS = 2200;
